import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ActivateAccount } from '@authentication/model/activation-account.model';
import { SnackbarService } from '@core/services/snackbar.service';
import { CustomSnackbarType } from '@shared/component/custom-snackbar/custom-snackbar.model';
import { RoutePath } from '@shared/route-path.enum';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

@Component({
	selector: 'sf-activate-account',
	templateUrl: './activate-account.component.html',
	styleUrls: ['./activate-account.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivateAccountComponent implements OnInit, OnDestroy {
	public RoutePath: typeof RoutePath = RoutePath;
	private token: string;
	public title: BehaviorSubject<string> = new BehaviorSubject<string>(null);
	public label: BehaviorSubject<string> = new BehaviorSubject<string>(null);
	public showButton: boolean = false;
	public orderNumber: string;

	#loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public loading$: Observable<boolean> = this.#loading.asObservable();

	private readonly destroy: Subject<void> = new Subject();

	constructor(
		private route: ActivatedRoute,
		private authService: AuthService,
		private snackbarService: SnackbarService
	) {}

	public ngOnInit(): void {
		this.token = this.route.snapshot.params.token;
		if (this.token) {
			this.activateAccount(this.token);
		}
	}

	public ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}

	private activateAccount(token: string): void {
		this.authService
			.activateAccount(token)
			.pipe(
				catchError((error) => {
					if (error.status === 400) {
						this.title.next('sf.module.auth.account-activation.title');
						this.label.next('sf.module.auth.account-activation.account_already_activated');
					}
					if (error.status === 404) {
						this.showButton = true;
						this.title.next('sf.module.auth.account-activation.link_expired.title');
						this.label.next('sf.module.auth.account-activation.link_expired.caption');
					}
					if (error.status === 422 || error.status === 500) {
						this.title.next('sf.module.auth.account-activation.something_went_wrong.title');
						this.label.next('sf.module.auth.account-activation.something_went_wrong.caption');
					}
					this.#loading.next(false);
					return EMPTY;
				}),
				tap((response: ActivateAccount) => {
					this.orderNumber = response.orderNumber;
					this.label.next('sf.module.auth.account-activation.caption_success');
					this.snackbarService.openSnackbar({
						message: 'sf.module.auth.account-activation.account_activated_successfully',
						type: CustomSnackbarType.Success,
					});
					this.#loading.next(false);
				}),
				take(1),
				takeUntil(this.destroy)
			)
			.subscribe();
	}
}
